import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/components/ui/Toast';
import { AppLayout, NotFoundLayout } from '@/components/AppLayout';
import { LoginPage } from '@/components/LoginPage';
import { SignupPage } from '@/components/SignupPage';
import { OAuthCallbackPage } from '@/components/OAuthCallbackPage';
import { AcceptTermsPage } from '@/components/AcceptTermsPage';
import { TermsOfServicePage } from '@/pages/TermsOfServicePage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { ProtectedRoute, RequireTermsAcceptance } from '@/components/ProtectedRoute';
import { ProtectedAdminRoute } from '@/components/ProtectedAdminRoute';
import { ProtectedProfileRoute } from '@/components/ProtectedProfileRoute';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { CampaignsWrapper } from '@/components/CampaignsWrapper';
import { CampaignDetails } from '@/components/CampaignDetails';
import { AccountsPage } from '@/components/AccountsPage';
import { ConnectionsPage } from '@/components/ConnectionsPage';
import { BrandsPage } from '@/components/brands/BrandsPage';
import { BrandDashboardPage } from '@/components/brands/BrandDashboardPage';
import { SpCampaignsPage } from '@/components/SpCampaignsPage';
import { SpCampaignDetailsPage } from '@/components/SpCampaignDetailsPage';
import { CreateCampaignPage } from '@/components/campaign-create/CreateCampaignPage';
import { DailySpendPage } from '@/components/DailySpendPage';
import { AudiencePage } from '@/components/audience/AudiencePage';
import { BudgetUsagePage } from '@/components/dashboard/BudgetUsagePage';
import { StreamPage, StreamV2Page } from '@/components/StreamPage';
import { ChatPage } from '@/components/ChatPage';
import { AgentPage } from '@/components/agent/AgentPage';
import { SapyonAgentPage } from '@/components/sapyonAgent/SapyonAgentPage';
import { AutomationsPage } from '@/components/automation/AutomationsPage';
import { SnapshotPage } from '@/components/snapshot/SnapshotPage';
import { WorkspacePage } from '@/components/workspace/WorkspacePage';
import { CampaignListPage } from '@/components/campaigns/CampaignListPage';
import { AnalysisPage } from '@/components/analysis/AnalysisPage';
import { NWordsPage } from '@/components/nWords/NWordsPage';
import { ShareOfVoicePage } from '@/components/shareOfVoice/ShareOfVoicePage';
import { ShareOfVoiceDetailPage } from '@/components/shareOfVoice/ShareOfVoiceDetailPage';
import { GlobalAnalysisPage } from '@/components/analysis/GlobalAnalysisPage';
import { ANALYSIS_ROUTE } from '@/components/analysis/reportAnalysisUi';
import { FlipkartDashboardPage } from '@/components/flipkart/FlipkartDashboardPage';
import { FlipkartLegacyCampaignRedirect } from '@/components/flipkart/FlipkartLegacyCampaignRedirect';
import { FlipkartReportPage } from '@/components/flipkart/FlipkartReportPage';
import { FlipkartWorkspacePage } from '@/components/flipkart/FlipkartWorkspacePage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminAccountMetricsPage } from '@/pages/admin/AdminAccountMetricsPage';
import { AdminReportCoveragePage } from '@/pages/admin/AdminReportCoveragePage';
import { AdminStreamSubscriptionsPage } from '@/pages/admin/AdminStreamSubscriptionsPage';
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { RetailPage } from '@/pages/RetailPage';
import { RetailDataPage } from '@/components/retail/RetailDataPage';
import { ReportPage } from '@/components/reporting/ReportPage';
import { ProtectedSuperAdminRoute } from '@/components/ProtectedSuperAdminRoute';
import { NotFoundPage } from '@/pages/NotFoundPage';

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            <Route path="/login/oauth/callback" element={<OAuthCallbackPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/accept-terms" element={<AcceptTermsPage />} />
              <Route element={<RequireTermsAcceptance />}>
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<DashboardWrapper />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/campaigns/:id" element={<CampaignDetails />} />
                <Route path="/campaigns" element={<CampaignsWrapper />} />
                <Route path="/connections" element={<ConnectionsPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route
                  path="/brands/:brandId/campaigns/dashboard"
                  element={<BrandDashboardPage />}
                />
                <Route
                  path="/brands/:brandId/campaigns"
                  element={<Navigate to="dashboard" replace />}
                />
                <Route
                  path="/brands/:brandId"
                  element={<Navigate to="campaigns/dashboard" replace />}
                />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path={ANALYSIS_ROUTE} element={<GlobalAnalysisPage />} />
                <Route
                  path="/flipkart/:credentialId/campaigns"
                  element={<FlipkartLegacyCampaignRedirect />}
                />
                <Route
                  path="/flipkart/:credentialId/campaigns/dashboard"
                  element={<FlipkartLegacyCampaignRedirect />}
                />
                <Route
                  path="/flipkart/:credentialId/campaigns/workspace"
                  element={<FlipkartLegacyCampaignRedirect />}
                />
                <Route
                  path="/flipkart/:credentialId/accounts/:adAccountId/campaigns"
                  element={<Navigate to="dashboard" replace />}
                />
                <Route
                  path="/flipkart/:credentialId/accounts/:adAccountId/campaigns/dashboard"
                  element={<FlipkartDashboardPage />}
                />
                <Route
                  path="/flipkart/:credentialId/accounts/:adAccountId/campaigns/workspace"
                  element={<FlipkartWorkspacePage />}
                />
                <Route
                  path="/flipkart/:credentialId/accounts/:adAccountId/campaigns/report"
                  element={<FlipkartReportPage />}
                />
                <Route element={<ProtectedAdminRoute />}>
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/account-metrics" element={<AdminAccountMetricsPage />} />
                </Route>
                <Route element={<ProtectedSuperAdminRoute />}>
                  <Route path="/admin/report-coverage" element={<AdminReportCoveragePage />} />
                  <Route path="/admin/stream-subscriptions" element={<AdminStreamSubscriptionsPage />} />
                  <Route path="/admin/settings" element={<AdminSettingsPage />} />
                  <Route path="/admin/retail/:credentialId" element={<RetailPage />} />
                </Route>
                <Route element={<ProtectedProfileRoute />}>
                <Route path="/accounts/:profileId/campaigns" element={<Navigate to="dashboard" replace />} />
                {/* Old campaigns Dashboard (SpCampaignsPage tab) — commented out; SnapshotPage is now Dashboard */}
                {/* <Route path="/accounts/:profileId/campaigns/dashboard" element={<SpCampaignsPage />} /> */}
                <Route path="/accounts/:profileId/campaigns/dashboard" element={<SnapshotPage />} />
                <Route
                  path="/accounts/:profileId/campaigns/overview"
                  element={<Navigate to="../dashboard" replace />}
                />
                <Route path="/accounts/:profileId/campaigns/workspace" element={<WorkspacePage />} />
                <Route path="/accounts/:profileId/campaigns/list" element={<CampaignListPage />} />
                <Route path="/accounts/:profileId/campaigns/analysis" element={<AnalysisPage />} />
                <Route path="/accounts/:profileId/campaigns/n-words" element={<NWordsPage />} />
                <Route path="/accounts/:profileId/campaigns/sov" element={<ShareOfVoicePage />} />
                <Route path="/accounts/:profileId/campaigns/sov/:keywordKey" element={<ShareOfVoiceDetailPage />} />
                <Route
                  path="/accounts/:profileId/campaigns/snapshot"
                  element={<Navigate to="../dashboard" replace />}
                />
                <Route path="/accounts/:profileId/campaigns/budget-rules" element={<SpCampaignsPage />} />
                <Route path="/accounts/:profileId/campaigns/automations" element={<AutomationsPage />} />
                <Route path="/accounts/:profileId/campaigns/reports" element={<SpCampaignsPage />} />
                <Route path="/accounts/:profileId/campaigns/report" element={<ReportPage />} />
                <Route path="/accounts/:profileId/campaigns/compare" element={<SpCampaignsPage />} />
                <Route path="/accounts/:profileId/campaigns/recommendations" element={<SpCampaignsPage />} />
                <Route path="/accounts/:profileId/campaigns/daily-spend" element={<DailySpendPage />} />
                <Route path="/accounts/:profileId/campaigns/budget-usage" element={<BudgetUsagePage />} />
                <Route path="/accounts/:profileId/campaigns/audience" element={<AudiencePage />} />
                <Route path="/accounts/:profileId/campaigns/stream" element={<StreamPage />} />
                <Route path="/accounts/:profileId/campaigns/stream-v2" element={<StreamV2Page />} />
                <Route path="/accounts/:profileId/campaigns/chat" element={<ChatPage />} />
                <Route path="/accounts/:profileId/campaigns/agent" element={<AgentPage />} />
                <Route path="/accounts/:profileId/campaigns/sapyon-agent" element={<SapyonAgentPage />} />
                <Route path="/accounts/:profileId/campaigns/retail" element={<RetailDataPage />} />
                <Route path="/accounts/:profileId/campaigns/new/:adProduct" element={<CreateCampaignPage />} />
                <Route path="/accounts/:profileId/campaigns/:campaignId" element={<SpCampaignDetailsPage />} />
                </Route>
              </Route>
              <Route element={<NotFoundLayout />}>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Route>
            </Route>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
